import React, { useEffect } from "react";
import { useLocalStore, useStoreState } from "easy-peasy";
import { Box, Link, Paper, TableRow, TableCell } from "@mui/material";
import SearchInput from "components/admin_v2/ui/SearchInput";
import Spinner from "components/admin_v2/ui/Spinner";
import PaginatedTable from "components/admin_v2/ui/PaginatedTable";
import TopPageLayout from "components/admin_v2/nav/TopPageLayout";
import VendorConfigTabs from "components/admin_v2/vendor_config/VendorConfigTabs";
import VendorStaffsFilter from "components/admin_v2/vendor_config/staff/VendorStaffsFilter";
import VendorSelector from "components/admin_v2/nav/VendorSelector";
import RouterLink from "components/admin_v2/nav/RouterLink";
import BatchSelect from "components/admin_v2/common/BatchSelect";
import GreenCheckbox from "components/admin_v2/ui/GreenCheckbox";
import { OutlinedBtn } from "components/admin_v2/ui/Buttons";
import { usersStore } from "components/admin_v2/common/stores/usersStore";
import { updateWithRouter, camelizeLocationSearch } from "lib/queryString";
import { pageRoutes } from "services/api";
import { batchArchive, batchUnarchive } from "services/apiUsers";
import useSorting from "lib/useSorting";
import I18n from "utils/i18n.js";
import useDebounce from "lib/useDebounce";
import useCommonPageStyles from "components/admin_v2/common/useCommonPageStyles";
import { formatPhone } from "lib/util";

const LABELS_I18N = "vendor_config.staff.labels";

const VendorStaffs = (props) => {
  const cls = useCommonPageStyles();
  const { vendorId } = useStoreState((s) => s.vendor);
  const store = useLocalStore(() =>
    usersStore({ ...camelizeLocationSearch(props), role: "vendor_staff" })
  );
  const [state, actions] = store;
  const searchInput = useDebounce(state.query);
  const updateQueryString = updateWithRouter(props);
  const { sortBy, sortableColumn } = useSorting({ field: "created_at", order: "desc" });

  let actionsList = [];
  if (state.scope == "archived") {
    actionsList.push({ label: I18n.t("ui.btn.unarchive"), action: batchUnarchive });
  } else {
    actionsList.push({ label: I18n.t("ui.btn.archive"), action: batchArchive });
  }

  useEffect(() => {
    actions.setFromRouter(camelizeLocationSearch(props));
  }, [props.location.search]);

  useEffect(() => {
    actions.fetchUsers({
      sortByField: sortBy.field,
      sortByOrder: sortBy.order,
      vendorId: vendorId
    });
  }, [searchInput, state.page, state.perPage, state.scope, state.refresh, sortBy, vendorId]);

  useEffect(() => {
    updateQueryString({ page: 1, query: searchInput });
  }, [searchInput]);

  const updatePage = (_e, newPage) => updateQueryString({ page: newPage + 1 });

  const updatePerPage = (e) => {
    updateQueryString({ page: 1, per_page: e.target.value });
  };

  const onSearch = (e) => {
    actions.setQuery(e.target.value);
  };

  const headers = [
    {
      field: "batchCheck",
      label: (
        <GreenCheckbox
          checked={state.allSelected}
          onChange={(e) => actions.updateAllSelected(e.target.checked)}
        />
      ),
      skipSorting: true
    },
    { field: "id", label: "ID" },
    { field: "first_name", label: I18n.t(`${LABELS_I18N}.first_name`) },
    { field: "last_name", label: I18n.t(`${LABELS_I18N}.last_name`) },
    { field: "system_user", label: I18n.t(`${LABELS_I18N}.system_user`) },
    { field: "Email", label: I18n.t(`${LABELS_I18N}.email`) },
    { field: "staff_role", label: I18n.t(`${LABELS_I18N}.staff_role`) },
    { field: "vendor", label: I18n.t(`${LABELS_I18N}.vendor`), skipSorting: true },
    { field: "phone_number", label: I18n.t(`${LABELS_I18N}.phone_number`), skipSorting: true },
    { field: "created_at", label: I18n.t(`${LABELS_I18N}.created_at`) },
    { field: "", label: "" }
  ].map(sortableColumn);

  const row = (user) => (
    <TableRow hover key={`user-${user.id}`}>
      <TableCell component="th" style={{ width: "5%" }}>
        <GreenCheckbox
          checked={state.isSelected(user.id)}
          onChange={(e) => actions.updateSelectedIds({ isAdd: e.target.checked, id: user.id })}
        />
      </TableCell>
      <TableCell component="th" style={{ width: "5%" }}>
        <RouterLink to={pageRoutes.vendorStaff(user.id)} className={cls.tableLink}>
          {user.id}
        </RouterLink>
      </TableCell>
      <TableCell>{user.firstName}</TableCell>
      <TableCell>{user.lastName}</TableCell>
      <TableCell>{user.systemUser ? I18n.t("ui.positive") : I18n.t("ui.negative")}</TableCell>
      <TableCell>{user.email}</TableCell>
      <TableCell>{user.staffRole}</TableCell>
      <TableCell>{user.vendor}</TableCell>
      <TableCell>{formatPhone(user.phoneNumber)}</TableCell>
      <TableCell>{user.createdAt}</TableCell>
      <TableCell>
        <RouterLink to={pageRoutes.vendorStaff(user.id)} className={cls.tableLink}>
          {I18n.t("ui.btn.view")}
        </RouterLink>
        <RouterLink to={pageRoutes.vendorStaffEdit(user.id)} className={cls.tableLink}>
          {I18n.t("ui.btn.edit")}
        </RouterLink>
        <Link
          onClick={() => actions.toggleArchive(user.id)}
          className={cls.tableLink}
          underline="hover"
        >
          {user.discarded ? I18n.t("ui.btn.unarchive") : I18n.t("ui.btn.archive")}
        </Link>
      </TableCell>
    </TableRow>
  );

  return (
    <React.Fragment>
      <TopPageLayout
        title={I18n.t("vendor_config.title")}
        actionButtons={[<VendorSelector key="vendor-select" />]}
      />
      <VendorConfigTabs />
      <Box className={cls.btnsWrapper}>
        <RouterLink className={cls.actionBtn} to={pageRoutes.vendorStaffNew()}>
          <OutlinedBtn>{I18n.t("vendor_config.new_staff.btn")}</OutlinedBtn>
        </RouterLink>
      </Box>
      <Paper className={cls.paper}>
        <Box className={cls.filters}>
          <BatchSelect store={store} actionsList={actionsList} />
          <VendorStaffsFilter state={state} updateQueryString={updateQueryString} />
          <SearchInput cls value={state.query} onChange={onSearch} />
        </Box>
        {state.loading ? (
          <Spinner />
        ) : (
          <PaginatedTable
            headers={headers}
            count={state.total}
            perPage={state.perPage}
            page={state.page}
            onPageChange={updatePage}
            onChangePerPage={updatePerPage}
          >
            {state.users.map(row)}
          </PaginatedTable>
        )}
      </Paper>
    </React.Fragment>
  );
};

export default VendorStaffs;
